/**
 * ImpersonationBanner - Banner permanent en mode impersonation
 * 
 * Affiche un banner jaune en haut de page quand un admin est connecté
 * en tant qu'utilisateur (impersonation mode).
 * 
 * Permet de revenir au compte admin via le bouton "Revenir à mon compte".
 * 
 * Conforme WCAG 2.1 AA
 */
import React from 'react';
import { AlertTriangle, LogOut, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getApiBase, getToken } from '../lib/utils';

interface ImpersonationBannerProps {
  adminEmail: string;
  targetEmail: string;
}

export default function ImpersonationBanner({ adminEmail, targetEmail }: ImpersonationBannerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const exitMutation = useMutation({
    mutationFn: async () => {
      const token = getToken();
      const response = await fetch(`${getApiBase()}/impersonation/exit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Échec de la sortie du mode impersonation');
      }
      
      return response.json();
    },
    onSuccess: (data: any) => {
      // Utiliser le nouveau token admin retourné par le backend (utiliser 'access_token' comme dans getToken())
      if (data.admin_token) {
        localStorage.setItem('access_token', data.admin_token);
      }
      
      // Utiliser le user admin retourné par le backend
      if (data.admin_user) {
        localStorage.setItem('user', JSON.stringify(data.admin_user));
      }
      
      // Nettoyer flags impersonation
      localStorage.removeItem('impersonation_active');
      localStorage.removeItem('impersonation_target_email');
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_user_backup');
      
      // Invalider toutes les queries
      queryClient.invalidateQueries();
      
      toast.success('Retour au compte admin');
      
      // Rediriger vers page admin users
      setTimeout(() => {
        navigate('/admin/users');
        window.location.reload(); // Force reload pour appliquer token admin
      }, 500);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erreur lors de la sortie du mode impersonation');
    },
  });

  const handleExit = () => {
    if (window.confirm('Voulez-vous vraiment revenir à votre compte admin ?')) {
      exitMutation.mutate();
    }
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-black px-4 py-3 shadow-lg"
      role="alert"
      aria-live="polite"
    >
      <div className="container mx-auto max-w-[1600px] flex items-center justify-between gap-4">
        {/* Message d'alerte */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <AlertTriangle 
            className="w-5 h-5 flex-shrink-0" 
            aria-hidden="true" 
          />
          <div className="min-w-0">
            <p className="font-semibold text-sm">
              Mode Impersonation Actif
            </p>
            <p className="text-xs truncate">
              Connecté en tant que <strong>{targetEmail}</strong> (admin: {adminEmail})
            </p>
          </div>
        </div>

        {/* Bouton retour */}
        <button
          onClick={handleExit}
          disabled={exitMutation.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-gray-100 px-4 py-2 text-sm font-medium text-black shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          aria-label="Revenir au compte admin"
        >
          {exitMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Sortie...
            </>
          ) : (
            <>
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Revenir à mon compte admin
            </>
          )}
        </button>
      </div>
    </div>
  );
}
